import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { testDataApi } from '../../api/testdata';

const DataFixture = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  onRunWithoutFixture,
  title = "Select Test Data Fixture",
  description = "Select a data fixture to use for this test run or run without a fixture."
}) => {
  const [fixtures, setFixtures] = useState([]);
  const [loadingFixtures, setLoadingFixtures] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchFixtures();
    }
  }, [isOpen]);

  const fetchFixtures = async () => {
    try {
      setLoadingFixtures(true);
      const data = await testDataApi.getAllFixtures();
      setFixtures(data);
      setLoadingFixtures(false);
    } catch (error) {
      console.error('Error fetching fixtures:', error);
      setLoadingFixtures(false);
    }
  };

  const handleRunWithFixture = () => {
    onSelect(selectedFixture);
    onClose();
  };

  const handleRunWithoutFixture = () => {
    setSelectedFixture(null);
    onRunWithoutFixture();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-sm w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{description}</p>
          <select
            value={selectedFixture?.id ?? ''}
            onChange={(e) => {
              const found = fixtures.find(f => String(f.id) === e.target.value);
              setSelectedFixture(found ?? null);
            }}
            disabled={loadingFixtures}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">select fixture</option>
            {fixtures.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          {loadingFixtures && (
            <p className="text-xs text-gray-400">Loading fixtures...</p>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleRunWithoutFixture}
            className="px-4 py-2 border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Run with no fixture
          </button>
          <button
            onClick={handleRunWithFixture}
            disabled={!selectedFixture}
            className={`px-4 py-2 rounded-md ${
              selectedFixture 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-green-300 text-white cursor-not-allowed'
            }`}
          >
            {selectedFixture 
              ? `Run with fixture` 
              : 'Select a fixture first'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataFixture;
